// src/pages/PositionSettings/PositionSettingsPage.jsx

import React, { useEffect, useState } from "react";
import { Form, Space, message, Spin, Button, Modal } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { LayoutGrid, ChevronDown, ChevronUp } from "lucide-react";
import positionSettingsApi from "../../api/positionSettingsApi";
import inventory from "../../api/inventoryApi";
import PositionSettingsModal from "./PositionSettingsModal";

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

const PROMO_TYPES = [
    { label: "Only Crystals", value: "onlyCrystals" },
    { label: "Crystals & Crypto", value: "crystalsAndCrypto" },
    { label: "Crypto Only", value: "cryptoOnly" },
];

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

function promoTypePill(promoType) {
    const label = PROMO_TYPES.find((p) => p.value === promoType)?.label || promoType;
    const tone =
        promoType === "onlyCrystals"
            ? "border-violet-400/30 bg-violet-500/12 text-violet-100"
            : promoType === "cryptoOnly"
              ? "border-amber-400/30 bg-amber-500/12 text-amber-100"
              : "border-sky-400/30 bg-sky-500/12 text-sky-100";
    return (
        <span className={cn("inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium", tone)}>
            {label}
        </span>
    );
}

export default function PositionSettingsPage() {
    const [form] = Form.useForm();
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [gemTypes, setGemTypes] = useState([]);
    const [gemLoading, setGemLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const data = await positionSettingsApi.getAllPositionSettings();
            setSettings(data);
        } catch (err) {
            console.error("Ошибка при загрузке настроек позиций:", err);
            message.error("Не удалось загрузить настройки позиций");
        } finally {
            setLoading(false);
        }
    };

    const fetchGemTypes = async () => {
        setGemLoading(true);
        try {
            const data = await inventory.gemCatalogApi.getAllGems();
            setGemTypes(data);
        } catch (err) {
            console.error("Ошибка при загрузке типов гемов:", err);
            message.error("Не удалось загрузить список типов гемов");
        } finally {
            setGemLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
        fetchGemTypes();
    }, []);

    const openModal = (record = null) => {
        setEditingRecord(record);
        if (record) {
            form.setFieldsValue({
                gemId: record.gemId,
                number: record.number,
                name: record.name,
                minParticipantsCount: record.minParticipantsCount,
                minPrizePlacesCount: record.minPrizePlacesCount,
                promoType: record.promoType,
                defaultBudget: record.defaultBudget,
                defaultTokenType: record.defaultTokenType,
                maxTasks: record.maxTasks ?? 5,
                presets: record.presets.map((p) => ({
                    win: p.win,
                    budget_percent: p.budget_percent,
                })),
            });
        } else {
            form.resetFields();
            form.setFieldsValue({ presets: [{ win: 0, budget_percent: 0 }] });
        }
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingRecord(null);
        form.resetFields();
    };

    const handleSubmit = async (values) => {
        const payload = {
            gemId: values.gemId,
            number: values.number,
            name: values.name,
            minParticipantsCount: values.minParticipantsCount,
            minPrizePlacesCount: values.minPrizePlacesCount,
            promoType: values.promoType,
            defaultBudget: values.defaultBudget,
            defaultTokenType: values.defaultTokenType,
            maxTasks: values.maxTasks ?? 5,
            presets: values.presets.map((item) => ({
                win: Number(item.win),
                budget_percent: Number(item.budget_percent),
            })),
        };

        try {
            if (editingRecord) {
                await positionSettingsApi.updatePositionSetting(editingRecord.id, payload);
                message.success("Настройка позиций обновлена");
            } else {
                await positionSettingsApi.createPositionSetting(payload);
                message.success("Настройка позиций создана");
            }
            closeModal();
            fetchSettings();
        } catch (err) {
            console.error("Ошибка при сохранении настройки позиций:", err);
            message.error("Не удалось сохранить настройку позиций");
        }
    };

    const handleDelete = (id) => {
        Modal.confirm({
            title: "Удалить настройку позиций?",
            content: "Запись будет удалена без восстановления.",
            okText: "Удалить",
            okType: "danger",
            cancelText: "Отмена",
            className: "custom-modal",
            centered: true,
            onOk: async () => {
                try {
                    await positionSettingsApi.deletePositionSetting(id);
                    message.success("Настройка позиций удалена");
                    fetchSettings();
                } catch (err) {
                    console.error(`Ошибка при удалении записи ID=${id}:`, err);
                    message.error("Не удалось удалить настройку позиций");
                }
            },
        });
    };

    const toggleExpand = (id) => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    const th = "whitespace-nowrap px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wide text-white/50 sm:px-4";
    const td = "px-3 py-3 text-[13px] text-white/85 sm:px-4";

    const initialLoad = (loading || gemLoading) && !settings.length;

    if (initialLoad) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                </section>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <LayoutGrid className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                        </span>
                        <div>
                            <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                                Position settings
                            </h1>
                            <p className="text-[12px] text-white/45">
                                Категории промо, пресеты победителей и бюджета
                            </p>
                        </div>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => openModal()}
                        className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                        size="large"
                    >
                        Добавить настройку
                    </Button>
                </div>
            </section>

            <section className={cn(shellClass, "relative flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {(loading || gemLoading) && settings.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/45 backdrop-blur-sm">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                )}
                <div className="min-h-0 flex-1 overflow-auto">
                    <table className="w-full min-w-[900px] border-collapse text-left">
                        <thead className="sticky top-0 z-[1] bg-[#0a0e18]/90 text-white/70 backdrop-blur-md">
                            <tr className="border-b border-white/10">
                                <th className={cn(th, "w-10")} />
                                <th className={th}>ID</th>
                                <th className={th}>Gem</th>
                                <th className={th}>Number</th>
                                <th className={th}>Name</th>
                                <th className={th}>Promo type</th>
                                <th className={th}>Default budget</th>
                                <th className={th}>Token</th>
                                <th className={cn(th, "text-right")}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {settings.map((item) => {
                                const gem = gemTypes.find((g) => g.id === item.gemId);
                                const isOpen = expandedId === item.id;
                                return (
                                    <React.Fragment key={item.id}>
                                        <tr
                                            className="cursor-pointer border-t border-white/[0.06] transition-colors hover:bg-white/[0.04]"
                                            onClick={() => toggleExpand(item.id)}
                                        >
                                            <td className={cn(td, "text-center text-white/45")}>
                                                {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                            </td>
                                            <td className={cn(td, "font-semibold text-emerald-300/90")}>{item.id}</td>
                                            <td className={td}>
                                                {gem ? (
                                                    <div className="flex items-center gap-2">
                                                        <img
                                                            src={`${IMAGE_BASE_URL}${gem.frontImage}`}
                                                            alt=""
                                                            className="h-8 w-8 object-contain"
                                                        />
                                                        <span className="max-w-[120px] truncate text-[12px] text-white/80">
                                                            {gem.name}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="text-white/45">{item.gemId}</span>
                                                )}
                                            </td>
                                            <td className={td}>{item.number}</td>
                                            <td className={cn(td, "font-medium text-white/90")}>{item.name}</td>
                                            <td className={td}>{promoTypePill(item.promoType)}</td>
                                            <td className={cn(td, "font-semibold text-emerald-300/90")}>
                                                {Number(item.defaultBudget).toFixed(2)}
                                            </td>
                                            <td className={td}>
                                                <span className="inline-flex rounded-full border border-white/12 bg-white/[0.06] px-2 py-0.5 text-[11px] text-white/75">
                                                    {item.defaultTokenType}
                                                </span>
                                            </td>
                                            <td className={cn(td, "text-right")} onClick={(e) => e.stopPropagation()}>
                                                <Space size="small">
                                                    <button
                                                        type="button"
                                                        onClick={() => openModal(item)}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-sky-300 transition hover:border-white/20 hover:bg-white/10"
                                                    >
                                                        <EditOutlined />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDelete(item.id)}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-rose-400 transition hover:border-rose-400/30 hover:bg-rose-500/10"
                                                    >
                                                        <DeleteOutlined />
                                                    </button>
                                                </Space>
                                            </td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="border-t border-white/[0.06] bg-white/[0.02]">
                                                <td colSpan={9} className="p-4 sm:p-5">
                                                    <h4 className="mb-3 text-[14px] font-semibold text-white/90">
                                                        Presets (%)
                                                    </h4>
                                                    <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                                                        <table className="w-full min-w-[400px] border-collapse text-[13px]">
                                                            <thead className="bg-white/[0.05] text-[11px] text-white/50">
                                                                <tr className="border-b border-white/10">
                                                                    <th className="px-3 py-2 text-left font-semibold">
                                                                        Icon
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left font-semibold">
                                                                        Winners (%)
                                                                    </th>
                                                                    <th className="px-3 py-2 text-left font-semibold">
                                                                        Budget (%)
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {item.presets.map((p, idx) => {
                                                                    const pos = item.presets.length - idx;
                                                                    const iconGem = gemTypes.find((g) => g.position === pos);
                                                                    return (
                                                                        <tr
                                                                            key={idx}
                                                                            className="border-t border-white/[0.06] hover:bg-white/[0.03]"
                                                                        >
                                                                            <td className="px-3 py-2">
                                                                                {iconGem ? (
                                                                                    <img
                                                                                        src={`${IMAGE_BASE_URL}${iconGem.frontImage}`}
                                                                                        alt=""
                                                                                        className="h-8 w-8 object-contain"
                                                                                    />
                                                                                ) : (
                                                                                    <span className="text-white/35">
                                                                                        —
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-3 py-2 text-white/80">
                                                                                {p.win.toFixed(2)}
                                                                            </td>
                                                                            <td className="px-3 py-2 font-medium text-emerald-300/85">
                                                                                {p.budget_percent.toFixed(2)}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                    {settings.length === 0 && !loading && !gemLoading && (
                        <div className="flex flex-col items-center py-16 text-white/45">
                            <LayoutGrid className="mb-2 h-12 w-12 opacity-40" />
                            <p className="text-[13px]">Нет настроек позиций</p>
                        </div>
                    )}
                </div>
            </section>

            <PositionSettingsModal
                open={modalOpen}
                onCancel={closeModal}
                onFinish={handleSubmit}
                form={form}
                editingRecord={editingRecord}
                gemTypes={gemTypes}
            />
        </div>
    );
}
